import { generateResponseFromGPT } from "../../adapters/insights/gencontent-adapter";
import { getSimplifiedDayTime } from "./WeekReviewHelper";

export const feelingWeightsScale = {
    "Awesome!": 3,
    "Good": 2,
    "Alright": 1,
    "Alright but slightly unpleasant": -0.5,
    "Meh": -1,
    "Drained": -1.5,
    "Not good": -2,
    "Awful": -3
};

// These are only meant for green flows
const energyWeightsScale = {
    'Low': 0.0,
    'Medium': 0.1,
    'High': 0.2
}

/**
 * Determines which flows should be highlighted as the peak and valley of a week. 
 * A peak means the most pleasant moment of a week, while a valley means the least pleasant moment of the week. 
 * Sometimes that's straightforward, like showing the single awesome or awful that hapened in a week.
 * Other times when there are multiple goods and bads, we choose the peak/valley by factoring how this moment was weighed down 
 * or elevated based on its surrounding flows.
 * @param {Array} flows - UI representation of flows
 * @returns {object} {
    description: A string describing when peak and valley happened
    peakIndices: Array of peak indices,
    valleyIndices: Array of valley indices,
    sortedPeakAndValleyFlows: Array of peaks and valleys in order of occurence
    orderedPeakValleyInfo: Array of objects describing peak valley order like {index: i, type: 'peak', dayTimeStr: 'Monday eve'}
    peaks: Array of flows corresponding to peak or null if no peak,
    valleys: Array of valleys corresponding to valley or null if no valley,
    dataForChart: Array of objs
}
*/
export function getPeakAndValley(flows) {
    const flowSummariesWithWeights = getPleasantnessTrendData(flows);
    const [peakIndices, valleyIndices] = getPeakAndValleyIndices(flowSummariesWithWeights);

    // get peak and valley flows in the order they happened
    const sortedPeakValleyIndices = [...peakIndices, ...valleyIndices].sort((a, b) => a - b);
    const sortedPeakAndValleyFlows = sortedPeakValleyIndices.map(i => flows[i]);

    const { description, orderedPeakValleyInfo } = getPeakValleyDescription(peakIndices, valleyIndices, flows);

    return {
        description: description,
        peakIndices: peakIndices,
        valleyIndices: valleyIndices,
        sortedPeakAndValleyFlows,
        orderedPeakValleyInfo,
        peaks: peakIndices.length ? peakIndices.map(index => flows[index]) : null,
        valleys: valleyIndices.length ? valleyIndices.map(index => flows[index]) : null,
        dataForChart: getDataForChart(flowSummariesWithWeights),
    };
}

/**
 * @returns {Array|string} An array of objects representing the description broken down in order. The obj has {text, style (if any)}
 * so the daytimes can be bolded by the component.
 * If there were multiple peaks and valleys, we create a placeholder text so we can later make a gpt request asyncronously
 */
function getPeakValleyDescription(peakIndices, valleyIndices, flows) {
    const peakAndValleyIndices = [
        ...(peakIndices?.map(i => {
            const dayTimeStr = getSimplifiedDayTime(flows[i].date);
            const dayStr = dayTimeStr.split(' ')[0];
            return { index: i, type: 'peak', day: dayStr, daytime: dayTimeStr }
        }) ?? []),
        ...(valleyIndices?.map(i => {
            const dayTimeStr = getSimplifiedDayTime(flows[i].date);
            const dayStr = dayTimeStr.split(' ')[0];
            return { index: i, type: 'valley', day: dayStr, daytime: dayTimeStr }
        }) ?? []),
    ]
    // sort indices in ascending order
    const orderOfOccurrence = peakAndValleyIndices.sort((a, b) => a.index - b.index);

    let description = generateDescriptionArray(orderOfOccurrence);

    return {
        description: orderOfOccurrence.length <= 3 ? description :
            `You had ${peakIndices.length > 1 ? "multiple peaks" : "one peak"} and ${valleyIndices.length > 1 ? "multiple valleys" : "one valley"} this week`,
        orderedPeakValleyInfo: orderOfOccurrence
    }
}

/**
 * @param {Array} sortedPeakAndValleyOccurences - An array of peak and valley objects sorted chronologically
 * @returns {string|null} A description from GPT of the peaks and valleys for the week.
 */
export async function getPeakValleyDescriptionFromGPT(sortedPeakAndValleyOccurences) {
    try {
        if (!sortedPeakAndValleyOccurences || sortedPeakAndValleyOccurences.length <= 2) {
            return null;
        }
        const prompt = `
            Please give me a coherent sentence telling a person when their peaks and valleys happened based on the given data. 
            Maintain the order in which things happened in your phrasing. Think about when to include the time of day vs when it's too much info. Don't exceed 25 words.
            Here's the data for reference: ${JSON.stringify(sortedPeakAndValleyOccurences)}
        `
        const response = await generateResponseFromGPT(prompt);
        if (!response || !response.ok) {
            return null;
        }
        let trend = await response.json();
        if (trend.length < 10) return null;
        return trend;
    }
    catch (error) {
        console.error(error)
        return null;
    }
}

/**
 * Logic from GPT
 * @param {Array} events An array of objects that describes the order peaks and valleys happened in. 
 * The object looks like {type: 'peak', daytime: 'Monday evening'} 
 * @returns {Array} An array of objects representing parts of description and how to style that text, like 
 * [{text: "a peak on"}, {text: 'Monday evening', style: 'bold}] 
 */
export function generateDescriptionArray(events) {
    let descriptionArray = [];
    let currentGroup = { type: events[0].type, daytimes: [events[0].daytime] };

    for (let i = 1; i < events.length; i++) {
        if (events[i].type === currentGroup.type) {
            currentGroup.daytimes.push(events[i].daytime);
        }
        else {
            pushGroup(descriptionArray, currentGroup, descriptionArray.length > 0);
            currentGroup = { type: events[i].type, daytimes: [events[i].daytime] };
        }
    }
    pushGroup(descriptionArray, currentGroup, descriptionArray.length > 0);

    return descriptionArray;
}

// GPT generated
function pushGroup(descriptionArray, group, addFollowedBy) {
    let text = addFollowedBy ? `, followed by a ${group.type} on ` : `a ${group.type} on `;

    // Add "a peak/valley on" as one text chunk
    descriptionArray.push({ text });

    // Add each daytime separately so they can be bolded
    group.daytimes.forEach((daytime, index) => {
        descriptionArray.push({ text: daytime, style: "bold" });

        if (index < group.daytimes.length - 1) {
            descriptionArray.push({ text: " and " });
        }
    });
}

/**
 * Gets data for D3 to make a line chart representing the pleasantness trend
 * @param {Array} flows - UI objects for flows 
 * @returns {Array} Gets an array of flow summary objects that look like {date, feelingWeight, energy, feeling, colorName}
 */
function getPleasantnessTrendData(flows) {
    const flowSummaries = flows.map((flow) => {
        const date = flow.date;
        const colorName = flow.color.name.toLowerCase();
        const energy = flow.entry.answeredQuestions[0].answer;
        // set the feeling to the feeling answer if present else set it to the energy level
        let feeling = flow.entry.answeredQuestions.find(question => question.category === "feeling")?.answer ?? energy;
        if (feeling === "Alright") {
            // distinguish between pleasant and unpleasant alright
            if (!colorName.includes('green')) {
                feeling = "Alright but slightly unpleasant"
            }
        }
        const feelingWeight = feelingWeightsScale[feeling] || 0;
        return { date, feelingWeight, energy, feeling, colorName };
    });
    return flowSummaries;
}

/**
 * Returns 2 arrays, one for peak indices and one for valley. If there isn't a peak or valley, then an empty array is returned for them
 */
function getPeakAndValleyIndices(flowSummaries) {
    let currentPeakStats = {
        feelingWeight: -Infinity,
        contextualWeight: -Infinity,
        indices: []
    };
    let currentValleyStats = {
        feelingWeight: Infinity,
        contextualWeight: Infinity,
        indices: []
    };

    flowSummaries.forEach((flowSummary, i) => {
        const contextualWeight = getContextualWeightForFlow(flowSummaries, i);
        checkAndUpdatePeakIfFlowMatches(currentPeakStats, flowSummary.feelingWeight, i, contextualWeight);
        checkAndUpdateValleyIfFlowMatches(currentValleyStats, flowSummary.feelingWeight, i, contextualWeight);
    });

    // peaks should only be greens and valleys should be red
    const peakIndices = currentPeakStats.indices.filter(index => flowSummaries[index].colorName.includes('green'));
    const valleyIndices = currentValleyStats.indices.filter(index => flowSummaries[index].colorName.includes('red'));

    return [peakIndices, valleyIndices];
}

/**
 * @param {Array} flowSummaries Array of objects with info about the feeling weights for each flow
 * @param {number} index of flow whose weight we want to determine based on surrounding flows
 * @returns 
 */
function getContextualWeightForFlow(flowSummaries, index) {
    // factor in the weights of surrounding flows
    let contextualWeight = flowSummaries[index].feelingWeight
        + (flowSummaries[index - 1]?.feelingWeight || 0)
        + (flowSummaries[index + 1]?.feelingWeight || 0);

    if (flowSummaries[index].colorName.includes('green')) {
        // factor in energy levels for a pleasant flow
        const energy = flowSummaries[index].energy;
        contextualWeight += energyWeightsScale[energy];
    }
    return contextualWeight;
}

/**
 * Evaluates the current peak stats against the given flow info and updates it if we have found a new peak
 * @param {object} currentPeakStats {feelingWeight, contextualWeight, indices} for the current peak
 * @param {number} flowFeelingWeight The weight of the flow based on just its feeling part, as mapped via the feeling weights scale
 * @param {number} flowIndex The index of the flow
 * @param {number} flowContextualWeight The weight of the flow when we factor in the influence of its surrounding flows
 */
function checkAndUpdatePeakIfFlowMatches(currentPeakStats, flowFeelingWeight, flowIndex, flowContextualWeight) {
    // Is flow eligible to be a peak? E.g. awesome always trumps good
    if (flowFeelingWeight > currentPeakStats.feelingWeight) {
        updateStats(currentPeakStats, flowFeelingWeight, flowContextualWeight, flowIndex);
    }
    else if (flowFeelingWeight === currentPeakStats.feelingWeight) {
        // e.g. if current peak is an awesome and this flow is also awesome, choose this one if it has the higher contextual weight.
        if (flowContextualWeight > currentPeakStats.contextualWeight) {
            updateStats(currentPeakStats, flowFeelingWeight, flowContextualWeight, flowIndex);
        }
        else if (flowContextualWeight === currentPeakStats.contextualWeight) {
            // include this flow to be among the peaks if their contextual weights are equal
            currentPeakStats.indices.push(flowIndex);
        }
    }
}

/*
* Evaluates the current valley stats against the given flow info and updates it if we have found a new valley
*/
function checkAndUpdateValleyIfFlowMatches(currentValleyStats, flowFeelingWeight, flowIndex, flowContextualWeight) {
    // Is flow eligible to be valley? E.g. awful trumps not good
    if (flowFeelingWeight < currentValleyStats.feelingWeight) {
        updateStats(currentValleyStats, flowFeelingWeight, flowContextualWeight, flowIndex);
    }
    else if (flowFeelingWeight === currentValleyStats.feelingWeight) {
        // when feeling is same compare contextual weights and choose the lower one
        if (flowContextualWeight < currentValleyStats.contextualWeight) {
            updateStats(currentValleyStats, flowFeelingWeight, flowContextualWeight, flowIndex);
        }
        else if (flowContextualWeight === currentValleyStats.contextualWeight) {
            currentValleyStats.indices.push(flowIndex);
        }
    }
}

/**
 * @param {object} stats The stats object to update. It can correspond to the peak or valley stats
 * @param {number} newFeelingWeight
 * @param {number} newContextualWeight 
 * @param {Array} newIndex Index of the flow that's replacing the current peak/valley
 */
function updateStats(stats, newFeelingWeight, newContextualWeight, newIndex) {
    stats.feelingWeight = newFeelingWeight;
    stats.contextualWeight = newContextualWeight;
    stats.indices = [newIndex];
}

function getDataForChart(flowSummaries) {
    let dataForChart = flowSummaries.map((flow) => {
        const data = { date: flow.date, weight: flow.feelingWeight };
        if (flow.colorName.includes('green')) {
            data.weight += energyWeightsScale[flow.energy];
        }
        return data;
    });

    // Pad data with 0 on the end, with date 1 min more than the last date so the curveBasisOpen in D3 is drawn properly
    let endDate = new Date(dataForChart[dataForChart.length - 1].date);
    endDate.setMinutes(endDate.getMinutes() + 1);
    dataForChart.push({ date: endDate, weight: 0 });

    // Also pad data with 0 on the start, with date 1 min less than the first date
    let startDate = new Date(dataForChart[0].date);
    startDate.setMinutes(startDate.getMinutes() - 1);
    dataForChart.unshift({ date: startDate, weight: 0 });

    return dataForChart
}

//#region Functions used by chart that plots peak and valley

/**
 * Find all array indicies where the target y value matches the object's y coordinate
 * @param {Array} arr - Array of objects with x and y coordinates
 * @param {Number} targetY - y coordinate to find all instances of in the array
 * @returns {Array} - array of indices
 */
export function findIndices(arr, targetY) {
    let indices = []
    arr.forEach((item, i) => {
        if (item.y === targetY) {
            indices.push(i);
        }
    });
    return indices;
}

/**
 * @param {Array} endpoints - Points on a d3 line curve containing x and y coordinates
 * @param {Array} peakIndices - Indices that should be representing peaks
 * @returns Modifies the input endpoints in place if necessary
 */
export function adjustPeakIfNecessary(endpoints, peakIndices, dataForChart) {
    // In D3 the lower the Y the higher it appears
    const minY = endpoints.reduce(
        (minY, coordinates) => Math.min(coordinates.y, minY),
        Infinity
    )
    const graphPeakIndices = findIndices(endpoints, minY);
    // check if graph peaks matches provided peak indices
    let indicesToMakePeakOnGraph = [];
    peakIndices.forEach((index) => {
        if (!graphPeakIndices.includes(index)) {
            indicesToMakePeakOnGraph.push(index);
        }
    })
    if (indicesToMakePeakOnGraph.length > 0) {
        // adjust all peaks with the new value so they all look like peaks
        const offset = getAdjustmentOffset(graphPeakIndices, peakIndices, dataForChart);
        //console.log(offset, peakIndices)
        peakIndices.forEach(i => {
            // raise it above the current peak on graph
            endpoints[i].y = minY - offset;
        });
        return;
    }

    // if the peaks on the graph matched the peak indices, then check if 
    // the peak still needs to be be raised to not have any visual competitors
    let closestCompetitorDelta = minY;
    endpoints.forEach((endpoint, i) => {
        if (peakIndices.includes(i)) {
            return;
        }
        closestCompetitorDelta = Math.min(closestCompetitorDelta, endpoint.y - minY);
    });
    if (closestCompetitorDelta < 10) {
        //console.log('adjusting to not compete', peakIndices);
        peakIndices.forEach(i => {
            endpoints[i].y = endpoints[i].y - Math.min(10, 10 - closestCompetitorDelta);
        });
    }
}

/**
 * Calculates how much the calculated peak should be adjusted to look like the visual peak, based on whether the feeling of the competitor is the same or not. 
 */
function getAdjustmentOffset(graphIndicesToCompare, targetIndices, dataForChart) {
    // the feelings for the each peak among peaks OR valley among valleys would be the 
    // same based on our logic, so pick the first one for comparison
    const feelingWeightOfTarget = dataForChart[targetIndices[0]].weight;
    // get the largest feeling weight among the given graph indices corresponding to peaks or valleys
    const maxFeelingWeightDrawn = graphIndicesToCompare.reduce(
        (max, index) => Math.max(dataForChart[index].weight, max),
        -Infinity
    )
    if (Math.abs(feelingWeightOfTarget - maxFeelingWeightDrawn) <= 0.2) {
        // this means feeling is same (but energy can be different), so raise slightly
        return 9;
    }
    else {
        return 15;
    }
}

/**
 * @param {Array} endpoints - Points on a d3 line curve containing x and y coordinates
 * @param {Array} valleyIndices - Indices that should be representing valleys
 * @returns Modifies the input endpoints in place if necessary
 */
export function adjustValleyIfNecessary(endpoints, valleyIndices, dataForChart) {
    const maxY = endpoints.reduce(
        (maxY, coordinates) => Math.max(coordinates.y, maxY),
        -Infinity
    )
    const graphValleyIndices = findIndices(endpoints, maxY);

    // check if graph valleys match calculated valley indices
    let indicesToMakeValleyOnGraph = [];
    valleyIndices.forEach((index) => {
        if (!graphValleyIndices.includes(index)) {
            indicesToMakeValleyOnGraph.push(index);
        }
    })
    if (indicesToMakeValleyOnGraph.length > 0) {
        const offset = getAdjustmentOffset(graphValleyIndices, valleyIndices, dataForChart);
        //console.log('valley offset', offset, valleyIndices);
        valleyIndices.forEach(i => {
            endpoints[i].y = maxY + offset;
        });

        return;
    }

    // if the valleys on the graph matched the indices provided, then check if 
    // they still need to be lowered to not have any visual competitors
    let closestCompetitorDelta = maxY;
    endpoints.forEach((endpoint, i) => {
        if (valleyIndices.includes(i)) {
            return;
        }
        closestCompetitorDelta = Math.min(closestCompetitorDelta, maxY - endpoint.y);
    });

    if (closestCompetitorDelta < 10) {
        //console.log('adjusting valley to not compete', valleyIndices, Math.min(10, 10 - closestCompetitorDelta))
        valleyIndices.forEach(i => {
            endpoints[i].y = endpoints[i].y + Math.min(10, 10 - closestCompetitorDelta);
        });
    }
}
//#endregion